<template>
    <Head title="Ticket SLA Policies" />

    <div class="py-6">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                <!-- Header -->
                <div class="mb-6">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center gap-3">
                            <div>
                                <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Ticket SLA Policies</h1>
                                <p class="mt-1 text-sm text-gray-600 dark:text-gray-300">Define service level agreements for helpdesk tickets based on category and priority</p>
                            </div>
                            <InfoButton title="Ticket SLA Policies Management" variant="default">
                                <p>
                                    <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                    SLA (Service Level Agreement) Policies define response time and resolution time targets for helpdesk
                                    tickets based on their category and priority. When a ticket is created, the system automatically
                                    matches it to the most specific applicable SLA policy to set time expectations. SLA tracking helps
                                    ensure timely support and identifies performance issues.
                                </p>
                                <p>
                                    <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                    • <strong>Add SLA Policy:</strong> Create policies with specific criteria (category, priority) and
                                      time targets<br>
                                    • <strong>Search:</strong> Filter policies by name<br>
                                    • <strong>Status Filter:</strong> View active or inactive policies<br>
                                    • <strong>Criteria Column:</strong> Shows the matching conditions (more specific = higher priority)<br>
                                    • <strong>Response Time:</strong> Hours until ticket must be acknowledged/assigned<br>
                                    • <strong>Resolution Time:</strong> Hours until ticket must be resolved<br>
                                    • <strong>Specificity Matching:</strong> Policies with more specific criteria (category + priority >
                                      priority only) are matched first<br>
                                    • <strong>Edit/Deactivate/Delete:</strong> Manage existing policies - changes only affect new tickets
                                </p>
                                <p>
                                    <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                    • <strong>Start with defaults</strong> - Create general policies for each priority level first<br>
                                    • <strong>Add specific overrides</strong> - Create more specific policies for critical categories<br>
                                    • <strong>Urgent gets shortest times</strong> - High priority tickets should have aggressive SLA
                                      targets<br>
                                    • <strong>Test matching logic</strong> - Create a test ticket to verify the correct policy is selected<br>
                                    • <strong>Review ticket counts</strong> - High counts indicate frequently used policies
                                </p>
                            </InfoButton>
                        </div>
                        <div v-if="canUpdateSettings" class="flex gap-2">
                            <button
                                @click="openDigestModal"
                                class="inline-flex items-center px-4 py-2 bg-gray-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 focus:bg-gray-700 active:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition ease-in-out duration-150"
                            >
                                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                SLA Digest
                            </button>
                            <Link
                                :href="route('settings.tickets.sla.create')"
                                class="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest focus:bg-blue-700 active:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition ease-in-out duration-150"
                            >
                                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                                </svg>
                                Add SLA Policy
                            </Link>
                        </div>
                    </div>
                </div>

                <!-- Filters -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <!-- Search -->
                        <div>
                            <label for="search" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
                            <input
                                id="search"
                                v-model="filters.search"
                                type="text"
                                placeholder="Search by name..."
                                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                @input="debouncedSearch"
                            />
                        </div>

                        <!-- Active Filter -->
                        <div>
                            <label for="active" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                            <select
                                id="active"
                                v-model="filters.active"
                                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                @change="search"
                            >
                                <option value="">All</option>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </select>
                        </div>

                        <!-- Reset Button -->
                        <div class="flex items-end">
                            <button
                                @click="resetFilters"
                                class="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                            >
                                Reset Filters
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Policies Table -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Policy Name
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Criteria
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Response Time
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Resolution Time
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Tickets
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Status
                                </th>
                                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            <tr v-for="policy in policies.data" :key="policy.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td class="px-6 py-4">
                                    <div class="flex items-center">
                                        <div>
                                            <div class="text-sm font-medium text-gray-900 dark:text-white">
                                                {{ policy.name }}
                                            </div>
                                            <div v-if="policy.description" class="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                                                {{ policy.description }}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4">
                                    <div class="text-sm text-gray-900 dark:text-white">
                                        <div v-if="policy.category" class="mb-1">
                                            <span class="font-medium">Category:</span> {{ policy.category.name }}
                                        </div>
                                        <div v-if="policy.priority" class="mb-1">
                                            <span class="font-medium">Priority:</span> {{ formatEnumValue(policy.priority) }}
                                        </div>
                                        <div v-if="!policy.category && !policy.priority" class="text-gray-500 dark:text-gray-400 italic">
                                            All tickets (default)
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    <span v-if="policy.response_time_hours">{{ policy.response_time_hours }} hrs</span>
                                    <span v-else class="text-gray-400">Not set</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    <span v-if="policy.resolution_time_hours">{{ policy.resolution_time_hours }} hrs</span>
                                    <span v-else class="text-gray-400">Not set</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {{ policy.tickets_count }} tickets
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        :class="[
                                            'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                                            policy.is_active
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                        ]"
                                    >
                                        {{ policy.is_active ? 'Active' : 'Inactive' }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div v-if="canUpdateSettings" class="flex items-center justify-end space-x-2">
                                        <Link
                                            :href="route('settings.tickets.sla.edit', policy.id)"
                                            class="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                        >
                                            Edit
                                        </Link>
                                        <button
                                            @click="toggleActive(policy)"
                                            class="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                                        >
                                            {{ policy.is_active ? 'Deactivate' : 'Activate' }}
                                        </button>
                                        <button
                                            v-if="policy.tickets_count === 0"
                                            @click="deletePolicy(policy)"
                                            class="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                    <div v-else class="text-sm text-gray-400">
                                        No actions available
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="policies.data.length === 0">
                                <td colspan="7" class="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                                    No SLA policies found. Create your first policy to define service level agreements for tickets.
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <!-- Pagination -->
                    <div v-if="policies.data.length > 0" class="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
                        <Pagination :links="policies.links" />
                    </div>
                </div>

                <!-- Info Box -->
                <div class="mt-6 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                    <div class="flex">
                        <div class="flex-shrink-0">
                            <svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                            </svg>
                        </div>
                        <div class="ml-3 flex-1">
                            <p class="text-sm text-blue-700 dark:text-blue-300">
                                <strong>How SLA policies work:</strong> When a ticket is created, the system automatically selects the most specific matching policy based on the ticket's category and priority. Policies with more specific criteria take precedence over general ones.
                            </p>
                        </div>
                    </div>
                </div>
        </div>
    </div>

    <!-- SLA Digest Modal -->
    <TransitionRoot appear :show="showDigestModal" as="template">
        <Dialog as="div" @close="closeDigestModal" class="relative z-50">
            <TransitionChild
                as="template"
                enter="duration-300 ease-out"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="duration-200 ease-in"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black/30 dark:bg-black/60" />
            </TransitionChild>

            <div class="fixed inset-0 overflow-y-auto">
                <div class="flex min-h-full items-center justify-center p-4 text-center">
                    <TransitionChild
                        as="template"
                        enter="duration-300 ease-out"
                        enter-from="opacity-0 scale-95"
                        enter-to="opacity-100 scale-100"
                        leave="duration-200 ease-in"
                        leave-from="opacity-100 scale-100"
                        leave-to="opacity-0 scale-95"
                    >
                        <DialogPanel class="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all border border-gray-200 dark:border-gray-700">
                            <DialogTitle
                                as="h3"
                                class="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4"
                            >
                                <div class="flex items-center">
                                    <svg class="w-6 h-6 mr-2 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    Ticket SLA Daily Digest Settings
                                </div>
                            </DialogTitle>

                            <div class="mt-4">
                                <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
                                    Configure who receives daily email digests summarizing at-risk or breached helpdesk tickets.
                                </p>

                                <form @submit.prevent="saveDigestSettings" class="space-y-6">
                                    <!-- Enable/Disable Toggle -->
                                    <div class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                                        <div>
                                            <label for="digest-enabled" class="text-sm font-medium text-gray-900 dark:text-white">
                                                Enable Daily Digest
                                            </label>
                                            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                Send automated daily SLA digest emails
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            @click="digestForm.enabled = !digestForm.enabled"
                                            :class="[
                                                digestForm.enabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700',
                                                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800'
                                            ]"
                                        >
                                            <span
                                                :class="[
                                                    digestForm.enabled ? 'translate-x-5' : 'translate-x-0',
                                                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                                                ]"
                                            />
                                        </button>
                                    </div>

                                    <div v-if="digestForm.enabled" class="space-y-6">
                                        <!-- Send Time -->
                                        <div>
                                            <label for="send-time" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Send Time
                                            </label>
                                            <select
                                                id="send-time"
                                                v-model="digestForm.send_time"
                                                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                                required
                                            >
                                                <option value="00:00">12:00 AM (Midnight)</option>
                                                <option value="01:00">1:00 AM</option>
                                                <option value="02:00">2:00 AM</option>
                                                <option value="03:00">3:00 AM</option>
                                                <option value="04:00">4:00 AM</option>
                                                <option value="05:00">5:00 AM</option>
                                                <option value="06:00">6:00 AM</option>
                                                <option value="07:00">7:00 AM</option>
                                                <option value="08:00">8:00 AM</option>
                                                <option value="09:00">9:00 AM</option>
                                                <option value="10:00">10:00 AM</option>
                                                <option value="11:00">11:00 AM</option>
                                                <option value="12:00">12:00 PM (Noon)</option>
                                                <option value="13:00">1:00 PM</option>
                                                <option value="14:00">2:00 PM</option>
                                                <option value="15:00">3:00 PM</option>
                                                <option value="16:00">4:00 PM</option>
                                                <option value="17:00">5:00 PM</option>
                                                <option value="18:00">6:00 PM</option>
                                                <option value="19:00">7:00 PM</option>
                                                <option value="20:00">8:00 PM</option>
                                                <option value="21:00">9:00 PM</option>
                                                <option value="22:00">10:00 PM</option>
                                                <option value="23:00">11:00 PM</option>
                                            </select>
                                            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                Time shown in {{ timezoneInfo?.display_name || 'your company timezone' }} ({{ timezoneInfo?.gmt_offset || 'local time' }}). Digests send on the hour.
                                            </p>
                                        </div>

                                        <!-- Only if items -->
                                        <div class="flex items-center">
                                            <input
                                                id="only-if-items"
                                                v-model="digestForm.only_if_items"
                                                type="checkbox"
                                                class="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                                            />
                                            <label for="only-if-items" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                                Only send digest if there are at-risk or breached tickets
                                            </label>
                                        </div>

                                        <!-- Include Assigned Users -->
                                        <div class="flex items-center">
                                            <input
                                                id="include-assigned"
                                                v-model="digestForm.include_assigned"
                                                type="checkbox"
                                                class="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                                            />
                                            <label for="include-assigned" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                                Include users assigned to at-risk or breached tickets
                                            </label>
                                        </div>

                                        <!-- Roles Selection -->
                                        <div>
                                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Send to Roles
                                            </label>
                                            <div class="max-h-48 overflow-y-auto space-y-2 p-3 border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700/50">
                                                <div v-for="role in availableRoles" :key="role.id" class="flex items-center">
                                                    <input
                                                        :id="`role-${role.id}`"
                                                        v-model="digestForm.roles"
                                                        :value="role.id"
                                                        type="checkbox"
                                                        class="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                                                    />
                                                    <label :for="`role-${role.id}`" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                                        {{ role.name }}
                                                        <span v-if="role.description" class="text-xs text-gray-500 dark:text-gray-400">
                                                            - {{ role.description }}
                                                        </span>
                                                    </label>
                                                </div>
                                                <div v-if="availableRoles.length === 0" class="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                                                    No roles available
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Users Selection -->
                                        <div>
                                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Send to Specific Users
                                            </label>
                                            <div class="max-h-48 overflow-y-auto space-y-2 p-3 border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700/50">
                                                <div v-for="user in availableUsers" :key="user.id" class="flex items-center">
                                                    <input
                                                        :id="`user-${user.id}`"
                                                        v-model="digestForm.users"
                                                        :value="user.id"
                                                        type="checkbox"
                                                        class="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                                                    />
                                                    <label :for="`user-${user.id}`" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                                        {{ user.name }}
                                                        <span class="text-xs text-gray-500 dark:text-gray-400">
                                                            ({{ user.email }})
                                                        </span>
                                                    </label>
                                                </div>
                                                <div v-if="availableUsers.length === 0" class="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                                                    No users available
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Action Buttons -->
                                    <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                                        <button
                                            type="button"
                                            @click="closeDigestModal"
                                            class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                            :disabled="isSubmittingDigest"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            v-if="digestForm.enabled"
                                            type="button"
                                            @click="sendTestDigest"
                                            class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                            :disabled="isSubmittingDigest"
                                        >
                                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                            </svg>
                                            Send Test Email
                                        </button>
                                        <button
                                            type="submit"
                                            class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                            :disabled="isSubmittingDigest"
                                        >
                                            <svg v-if="isSubmittingDigest" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Save Settings
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import { ref } from 'vue';
import { Head, Link, router, usePage } from '@inertiajs/vue3';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { debounce } from 'lodash';
import Pagination from '@/Components/Pagination.vue';
import { useToast } from '@/Composables/useToast';
import { useTimezone } from '@/Composables/useTimezone';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    policies: Object,
    filters: Object,
    digestSettings: {
        type: Object,
        default: () => ({
            enabled: false,
            roles: [],
            users: [],
            include_assigned: false,
            send_time: '08:00',
            only_if_items: true,
        }),
    },
    availableRoles: {
        type: Array,
        default: () => [],
    },
    availableUsers: {
        type: Array,
        default: () => [],
    },
    canUpdateSettings: {
        type: Boolean,
        required: true,
    },
});

const page = usePage();
const toast = useToast();
const { toUTC, formatTime, timezoneInfo } = useTimezone();

const filters = ref({
    search: props.filters.search || '',
    active: props.filters.active || '',
});

const search = () => {
    router.get(route('settings.tickets.sla.index'), filters.value, {
        preserveState: true,
        preserveScroll: true,
    });
};

const debouncedSearch = debounce(search, 300);

const resetFilters = () => {
    filters.value = {
        search: '',
        active: '',
    };
    search();
};

const formatEnumValue = (value) => {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
};

const toggleActive = (policy) => {
    router.post(
        route('settings.tickets.sla.toggleActive', policy.id),
        {},
        {
            preserveScroll: true,
            onSuccess: () => {
                toast.success(`Policy ${policy.is_active ? 'deactivated' : 'activated'} successfully`);
            },
            onError: () => {
                toast.error('Failed to update policy status');
            },
        }
    );
};

const deletePolicy = (policy) => {
    if (!confirm(`Are you sure you want to delete "${policy.name}"? This action cannot be undone.`)) {
        return;
    }

    router.delete(route('settings.tickets.sla.destroy', policy.id), {
        preserveScroll: true,
        onSuccess: () => {
            toast.success('Policy deleted successfully');
        },
        onError: (errors) => {
            toast.error(errors.error || 'Failed to delete policy');
        },
    });
};

// SLA Digest Modal
const showDigestModal = ref(false);
const isSubmittingDigest = ref(false);
const digestForm = ref({
    enabled: props.digestSettings.enabled || false,
    roles: props.digestSettings.roles || [],
    users: props.digestSettings.users || [],
    include_assigned: props.digestSettings.include_assigned || false,
    send_time: props.digestSettings.send_time || '08:00',
    only_if_items: props.digestSettings.only_if_items !== undefined ? props.digestSettings.only_if_items : true,
});

const openDigestModal = () => {
    // Reset form to current settings
    let displaySendTime = '08:00';

    // Convert UTC send time to company timezone for display
    if (props.digestSettings.send_time) {
        const utcTime = props.digestSettings.send_time; // e.g., "08:00" in UTC
        const [hours, minutes] = utcTime.split(':');

        // Create a date in UTC with the stored time
        const utcDate = new Date();
        utcDate.setUTCHours(parseInt(hours), parseInt(minutes), 0, 0);

        // Format in company timezone to get local time
        displaySendTime = formatTime(utcDate);
    }

    digestForm.value = {
        enabled: props.digestSettings.enabled || false,
        roles: props.digestSettings.roles || [],
        users: props.digestSettings.users || [],
        include_assigned: props.digestSettings.include_assigned || false,
        send_time: displaySendTime,
        only_if_items: props.digestSettings.only_if_items !== undefined ? props.digestSettings.only_if_items : true,
    };
    showDigestModal.value = true;
};

const closeDigestModal = () => {
    showDigestModal.value = false;
};

const saveDigestSettings = () => {
    isSubmittingDigest.value = true;

    // Convert company timezone to UTC before saving
    const localTime = digestForm.value.send_time; // e.g., "08:00" in company timezone
    const [hours, minutes] = localTime.split(':');

    // Create a date in company timezone with the selected time
    const localDate = new Date();
    localDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    // Convert to UTC
    const utcDate = toUTC(localDate);
    const utcHours = utcDate.getUTCHours().toString().padStart(2, '0');
    const utcMinutes = utcDate.getUTCMinutes().toString().padStart(2, '0');
    const utcTime = `${utcHours}:${utcMinutes}`;

    router.post(route('settings.settings.tickets.sla.sla-digest.store'), {
        ...digestForm.value,
        send_time: utcTime, // Send UTC time to backend
        _company_id: page.props.company_id
    }, {
        preserveScroll: true,
        onSuccess: () => {
            toast.success('Digest settings saved successfully');
            closeDigestModal();
        },
        onError: () => {
            toast.error('Failed to save digest settings');
        },
        onFinish: () => {
            isSubmittingDigest.value = false;
        },
    });
};

const sendTestDigest = () => {
    isSubmittingDigest.value = true;

    router.post(route('settings.settings.tickets.sla.sla-digest.test'), {
        _company_id: page.props.company_id
    }, {
        preserveScroll: true,
        onSuccess: () => {
            toast.success('Test email sent successfully');
        },
        onError: () => {
            toast.error('Failed to send test email');
        },
        onFinish: () => {
            isSubmittingDigest.value = false;
        },
    });
};
</script>
